import { Plus, Boxes, ChevronDown } from 'lucide-react';
import type { Station } from '@hcc/types';
import { KitItemRow } from './KitItemRow';
import { Button } from '../ui/Button';
import type { useKitIssueItems } from './useKitIssueItems';

/**
 * The "Items to issue" block of the kit drawer: the seeded/editable item
 * rows plus the collapsible add-more controls. Split out of KitIssueDrawer
 * to keep that file under the size cap.
 */
export function KitItemsSection({
  kit,
  station,
  showAddKit,
  onToggleAddKit,
  onOpenPicker,
}: {
  kit: ReturnType<typeof useKitIssueItems>;
  station: Station;
  showAddKit: boolean;
  onToggleAddKit: () => void;
  onOpenPicker: () => void;
}) {
  return (
    <>
      <div className="mt-6 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-fg">Items to issue</h3>
        <span className="text-xs text-fg-muted">
          {kit.items.length} {kit.items.length === 1 ? 'item' : 'items'}
        </span>
      </div>

      <div className="mt-3 space-y-2">
        {kit.items.map((it, idx) => (
          <KitItemRow
            key={idx}
            item={it}
            station={station}
            category={it.asset_id ? (kit.assetCategoryById[it.asset_id] ?? null) : null}
            onPatch={(patch) => kit.setItem(idx, patch)}
            onRemove={() => (it.asset_id ? kit.removeAssetRow(it.asset_id) : kit.removeItem(idx))}
          />
        ))}
        {kit.items.length === 0 ? (
          <div className="rounded-token-sm border border-dashed border-line-strong/30 px-4 py-6 text-center text-sm text-fg-muted">
            No items yet. Add the first one below.
          </div>
        ) : null}
      </div>

      <div className="mt-4 border-t border-divider/10 pt-3">
        <button
          type="button"
          onClick={onToggleAddKit}
          aria-expanded={showAddKit}
          className="flex w-full items-center gap-1.5 text-xs font-medium text-fg-muted transition-colors hover:text-fg"
        >
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${showAddKit ? '' : '-rotate-90'}`}
          />
          Issue additional kit
        </button>
        {showAddKit ? (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              iconLeft={<Plus className="h-3.5 w-3.5" />}
              onClick={kit.addItem}
            >
              Add item manually
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              iconLeft={<Boxes className="h-3.5 w-3.5" />}
              onClick={onOpenPicker}
            >
              Pick from pool
            </Button>
          </div>
        ) : null}
      </div>
    </>
  );
}
